import {
	billKindSchema,
	billOfferSchema,
	billStatusSchema,
	checkedGstinSchema,
	gstModeSchema,
	orderStatusSchema,
	orderTypeSchema,
	paymentMethodSchema,
	paymentStatusSchema,
	phoneSchema,
} from "@luckymenu/core";
import { z } from "zod";
import { billPrefixSchema } from "./bill-number";
import { billCalculationSchema, billSnapshotSchema, discountRuleSchema } from "./snapshot";
import { upiVpaSchema } from "./upi";

/**
 * Shapes of the billing procedures, `staff.billing.*` and `public.billing.*`.
 * See docs/architecture/api.md, Billing. They live here rather than in
 * packages/core because they carry the bill calculation and snapshot schemas,
 * which belong to billing-core (as the site editor's shapes live in site-kit).
 */

const optionalText = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.transform((v) => (v === "" ? null : v))
		.nullable()
		.optional();

// ---------------------------------------------------------------------------
// Discounts (BL-07): percent or amount, on the bill or on an item, with who
// and why recorded. Who is the caller; why is `reason`.
// ---------------------------------------------------------------------------

/** A discount as staff enter it. The reason is kept on the bill and in the audit log. */
export const staffDiscountSchema = z.object({
	rule: discountRuleSchema,
	/** What the bill prints. Empty or left out prints "Discount". */
	label: optionalText(60),
	reason: z.string().trim().min(1).max(200),
});
export type StaffDiscount = z.input<typeof staffDiscountSchema>;

const uniqueItems = (discounts: { orderItemId: string }[]) =>
	new Set(discounts.map((d) => d.orderItemId)).size === discounts.length;

/**
 * The discounts of a bill preview or a finalise call, and the offers staff
 * changed (P9-T10). A staff item discount replaces that item's offer; a
 * staff bill discount replaces the bill offer.
 */
export const billDiscountsShape = {
	billDiscount: staffDiscountSchema.nullable().optional(),
	/** At most one per order item. */
	itemDiscounts: z
		.array(z.object({ orderItemId: z.uuid(), discount: staffDiscountSchema }))
		.max(100)
		.refine(uniqueItems, "One discount per item")
		.default([]),
	/** Offers staff took off this bill: item offers and bill offers alike. */
	removedOfferIds: z.array(z.uuid()).max(50).default([]),
	/**
	 * The bill offer or coupon: left out, the one that takes the most off;
	 * null, none; an ID, that one, if the bill can have it (else `CONFLICT`
	 * `offer_not_eligible`).
	 */
	billOfferId: z.uuid().nullable().optional(),
};

// ---------------------------------------------------------------------------
// Preview (P4-T06).
// ---------------------------------------------------------------------------

/** `staff.billing.preview`: the bill as it would be now, with these discounts. */
export const billPreviewInputSchema = z.object({ orderId: z.uuid(), ...billDiscountsShape });
export type BillPreviewInput = z.input<typeof billPreviewInputSchema>;

export const billPreviewSchema = z.object({
	orderId: z.uuid(),
	orderNo: z.number().int(),
	orderType: orderTypeSchema,
	orderStatus: orderStatusSchema,
	tableLabel: z.string().nullable(),
	/** The document the restaurant's GST mode issues. */
	kind: billKindSchema,
	/** Lines (the order's active items), discounts, service charge, taxes and totals. */
	calculation: billCalculationSchema,
	/** The bill offers and coupon this bill can have, most off first, for staff to choose. */
	eligibleOffers: z.array(billOfferSchema),
	/** As in the input: offers staff took off. */
	removedOfferIds: z.array(z.uuid()),
});
export type BillPreview = z.infer<typeof billPreviewSchema>;

// ---------------------------------------------------------------------------
// Finalise (P4-T07) and the bill as staff see it.
// ---------------------------------------------------------------------------

/**
 * Optional customer details printed on the bill (BL-11). Left out, the
 * order's name and phone are used; `null` leaves one off.
 */
export const billCustomerInputSchema = z.object({
	name: optionalText(60),
	phone: phoneSchema.nullable().optional(),
	/** A business diner's GSTIN, for their input tax credit. */
	gstin: checkedGstinSchema.nullable().optional(),
});

/**
 * `staff.billing.finalize`. A second call for the same order returns the
 * bill already made. With `expectedTotalPaise` (the preview's total), a bill
 * that would come out different is refused with the fresh preview.
 */
export const finalizeBillInputSchema = z.object({
	orderId: z.uuid(),
	...billDiscountsShape,
	customer: billCustomerInputSchema.optional(),
	expectedTotalPaise: z.number().int().min(0).optional(),
});
export type FinalizeBillInput = z.input<typeof finalizeBillInputSchema>;

export const paymentSchema = z.object({
	id: z.uuid(),
	billId: z.uuid(),
	method: paymentMethodSchema,
	amountPaise: z.number().int(),
	status: paymentStatusSchema,
	reference: z.string().nullable(),
	note: z.string().nullable(),
	receivedBy: z.uuid().nullable(),
	receivedAt: z.iso.datetime(),
	reversedAt: z.iso.datetime().nullable(),
	reversedBy: z.uuid().nullable(),
	reverseReason: z.string().nullable(),
});
export type Payment = z.infer<typeof paymentSchema>;

/** A finalised or void bill with its payments. */
export const billSchema = z.object({
	id: z.uuid(),
	orderId: z.uuid(),
	billNo: z.string(),
	series: z.string(),
	fy: z.string(),
	seq: z.number().int(),
	kind: billKindSchema,
	status: billStatusSchema,
	/** YYYY-MM-DD, the business date it was finalised on. */
	businessDate: z.string(),
	subtotalPaise: z.number().int(),
	discountPaise: z.number().int(),
	serviceChargePaise: z.number().int(),
	taxablePaise: z.number().int(),
	cgstPaise: z.number().int(),
	sgstPaise: z.number().int(),
	roundOffPaise: z.number().int(),
	totalPaise: z.number().int(),
	customerName: z.string().nullable(),
	customerPhone: z.string().nullable(),
	customerGstin: z.string().nullable(),
	finalizedBy: z.uuid().nullable(),
	finalizedAt: z.iso.datetime(),
	voidReason: z.string().nullable(),
	voidedBy: z.uuid().nullable(),
	voidedAt: z.iso.datetime().nullable(),
	/** The public bill page, `https://<slug>.<root>/b/<shareToken>`, to share (OP-14). */
	shareUrl: z.string(),
	/** Every payment, reversed ones included, oldest first. */
	payments: z.array(paymentSchema),
	/** Confirmed payments. */
	paidPaise: z.number().int(),
	/** Total − paid. 0 when covered, and for a void bill. */
	balancePaise: z.number().int(),
	/**
	 * `upi://pay` for the balance to the restaurant's UPI ID, for the full
	 * screen QR (P4-T18). Null without a UPI ID, a balance or on a void bill.
	 */
	upiLink: z.string().nullable(),
	/** Everything to show or reprint the bill. */
	snapshot: billSnapshotSchema,
});
export type Bill = z.infer<typeof billSchema>;

// ---------------------------------------------------------------------------
// Payments (P4-T09). LuckyMenu records what the restaurant received; it
// never holds diners' money (BL-10).
// ---------------------------------------------------------------------------

/** What staff record by hand. `razorpay` payments will be recorded by the integration. */
export const MANUAL_PAYMENT_METHODS = ["cash", "upi", "card", "other"] as const;

/**
 * `staff.billing.recordPayment`. `id` is made by the client: the same call
 * again returns the bill and records nothing. A split payment is one call
 * per part. For cash the amount is what the bill takes, not what the diner
 * handed over: the app shows the change.
 */
export const recordPaymentInputSchema = z
	.object({
		id: z.uuid(),
		billId: z.uuid(),
		method: z.enum(MANUAL_PAYMENT_METHODS),
		amountPaise: z.number().int().min(1),
		/** A UPI transaction or card slip number, if staff note one. */
		reference: optionalText(60),
		/** Required for `other`, such as the name of a credit customer. */
		note: optionalText(200),
	})
	.refine((p) => p.method !== "other" || Boolean(p.note), {
		message: "Say what it was",
		path: ["note"],
	});
export type RecordPaymentInput = z.input<typeof recordPaymentInputSchema>;

/** `staff.billing.reversePayment`: a payment recorded by mistake, or given back. */
export const reversePaymentInputSchema = z.object({
	paymentId: z.uuid(),
	reason: z.string().trim().min(1).max(200),
});
export type ReversePaymentInput = z.input<typeof reversePaymentInputSchema>;

/**
 * `staff.billing.void` (OP-15): the bill keeps its number and is marked
 * void; the order is open again, to change and bill anew.
 */
export const voidBillInputSchema = z.object({
	billId: z.uuid(),
	reason: z.string().trim().min(1).max(200),
});
export type VoidBillInput = z.input<typeof voidBillInputSchema>;

// ---------------------------------------------------------------------------
// Billing settings (MG-04, P4-T12). Tax rates are tax classes, edited in
// the menu (`staff.catalog.upsertTaxClass`).
// ---------------------------------------------------------------------------

/** An FSSAI licence or registration number: 14 digits. */
export const fssaiNoSchema = z
	.string()
	.trim()
	.regex(/^\d{14}$/, "14 digits");

export const billingSettingsSchema = z.object({
	gstMode: gstModeSchema,
	gstin: z.string().nullable(),
	fssaiNo: z.string().nullable(),
	pricesIncludeTax: z.boolean(),
	/** 0 for none (Q16: off by default). */
	serviceChargeBp: z.number().int(),
	roundOff: z.boolean(),
	/** Up to 5 characters before the financial year in bill numbers. Null: none. */
	billPrefix: z.string().nullable(),
	billHeader: z.string().nullable(),
	billFooter: z.string().nullable(),
	upiVpa: z.string().nullable(),
	/** What UPI apps show. Null: the restaurant's name. */
	upiPayeeName: z.string().nullable(),
	/** The most a cashier may take off a bill, as a share of the subtotal. */
	cashierDiscountLimitBp: z.number().int(),
	/** The default tax class's rate, which also taxes the service charge. Read only here. */
	defaultTaxRateBp: z.number().int(),
	updatedAt: z.iso.datetime(),
});
export type BillingSettings = z.infer<typeof billingSettingsSchema>;

/** Empty text clears an optional field. */
const cleared = <T extends z.ZodType>(schema: T) =>
	z.union([z.literal("").transform(() => null), schema.nullable()]).optional();

/**
 * `staff.billing.updateSettings`: fields left out keep their value. The GSTIN
 * is checked with its check character, the UPI ID as `name@provider`, the
 * FSSAI number as 14 digits and the prefix as at most 5 letters, digits, `-`
 * or `/`. The composition and regular modes need a GSTIN.
 */
export const updateBillingSettingsInputSchema = z.object({
	gstMode: gstModeSchema.optional(),
	gstin: cleared(checkedGstinSchema),
	fssaiNo: cleared(fssaiNoSchema),
	pricesIncludeTax: z.boolean().optional(),
	serviceChargeBp: z.number().int().min(0).max(10000).optional(),
	roundOff: z.boolean().optional(),
	billPrefix: cleared(z.string().trim().pipe(billPrefixSchema)),
	billHeader: cleared(z.string().trim().max(200)),
	billFooter: cleared(z.string().trim().max(200)),
	upiVpa: cleared(upiVpaSchema),
	upiPayeeName: cleared(z.string().trim().max(50)),
	cashierDiscountLimitBp: z.number().int().min(0).max(10000).optional(),
});
export type UpdateBillingSettingsInput = z.input<typeof updateBillingSettingsInputSchema>;

// ---------------------------------------------------------------------------
// The bills list and a bill's page (P4-T15).
// ---------------------------------------------------------------------------

const businessDateSchema = z.iso.date();

/**
 * `staff.billing.listBills`: newest first, `limit` at a time; pass the last
 * page's `nextCursor` for more. Filters: status, business dates (both
 * ends included), an order, and part of the bill number (ignoring case).
 */
export const listBillsInputSchema = z
	.object({
		cursor: z.uuid().optional(),
		limit: z.number().int().min(1).max(100).default(50),
		status: billStatusSchema.optional(),
		from: businessDateSchema.optional(),
		to: businessDateSchema.optional(),
		orderId: z.uuid().optional(),
		/** Part of a bill number, such as "123" or "sg2627". */
		search: z
			.string()
			.trim()
			.regex(/^[A-Za-z0-9/-]{1,16}$/, "Letters, digits, - and / of a bill number")
			.optional(),
	})
	.refine((i) => !i.from || !i.to || i.from <= i.to, {
		message: "The start must not be after the end",
		path: ["to"],
	});
export type ListBillsInput = z.input<typeof listBillsInputSchema>;

/** A row of the bills list. */
export const billSummarySchema = z.object({
	id: z.uuid(),
	orderId: z.uuid(),
	billNo: z.string(),
	kind: billKindSchema,
	status: billStatusSchema,
	businessDate: z.string(),
	finalizedAt: z.iso.datetime(),
	totalPaise: z.number().int(),
	paidPaise: z.number().int(),
	balancePaise: z.number().int(),
	customerName: z.string().nullable(),
	/** From the snapshot: the daily order number (a takeaway's token) and the table. */
	orderNo: z.number().int(),
	tableLabel: z.string().nullable(),
});
export type BillSummary = z.infer<typeof billSummarySchema>;

export const listBillsResultSchema = z.object({
	items: z.array(billSummarySchema),
	nextCursor: z.uuid().nullable(),
});
export type ListBillsResult = z.infer<typeof listBillsResultSchema>;

/** `staff.billing.getBill`: by its ID, or an order's final bill. */
export const getBillInputSchema = z
	.object({ billId: z.uuid().optional(), orderId: z.uuid().optional() })
	.refine((i) => (i.billId === undefined) !== (i.orderId === undefined), {
		message: "Give a bill or an order",
		path: ["billId"],
	});
export type GetBillInput = z.input<typeof getBillInputSchema>;

// ---------------------------------------------------------------------------
// Diners (P4-T14): `public.billing.*`. No staff IDs, phone numbers or
// discount reasons (those are for the restaurant's records).
// ---------------------------------------------------------------------------

/** The bill page `/b/<shareToken>` (OP-14): a finalised or void bill as printed. */
export const publicBillSchema = z.object({
	billNo: z.string(),
	kind: billKindSchema,
	/** A void bill is shown as void. */
	status: billStatusSchema,
	businessDate: z.string(),
	finalizedAt: z.iso.datetime(),
	/** For showing `finalizedAt` in the restaurant's time. */
	timeZone: z.string(),
	seller: billSnapshotSchema.shape.seller.omit({ upi: true }),
	customer: z.object({ name: z.string().nullable(), gstin: z.string().nullable() }),
	order: billSnapshotSchema.shape.order,
	/** Discount reasons are left out; labels stay. */
	calculation: billCalculationSchema,
	paidPaise: z.number().int(),
	balancePaise: z.number().int(),
	/** Pay the balance by UPI; null when nothing is due, the bill is void or there is no UPI ID. */
	upiLink: z.string().nullable(),
	payeeName: z.string().nullable(),
});
export type PublicBill = z.infer<typeof publicBillSchema>;

export const sharedBillInputSchema = z.object({ shareToken: z.string().min(1).max(64) });

/**
 * `public.billing.getRunningBill` (SF-11): the table's live order as a bill.
 * Open: the active items as the bill would be now, without discounts. Billed:
 * the issued bill with its discounts and what is paid. Null when the table
 * has no live order.
 */
export const runningBillSchema = z.object({
	orderId: z.uuid(),
	orderNo: z.number().int(),
	status: z.enum(["open", "billed"]),
	calculation: billCalculationSchema,
	/** Once staff issued the bill: its number, and the link to its page. */
	bill: z.object({ billNo: z.string(), kind: billKindSchema, shareToken: z.string() }).nullable(),
	paidPaise: z.number().int(),
	balancePaise: z.number().int(),
	/** "Pay with UPI" for the balance (SF-12). Without a bill number while the order is open. */
	upi: z.object({ payeeName: z.string(), link: z.string() }).nullable(),
	/** When someone at the table last tapped "I have paid", until staff record a payment. */
	paymentClaimedAt: z.iso.datetime().nullable(),
});
export type RunningBill = z.infer<typeof runningBillSchema>;

/** `public.billing.notifyPaid`: "I have paid". A repeat within 60 seconds is merged. */
export const notifyPaidResultSchema = z.object({
	claimedAt: z.iso.datetime(),
	merged: z.boolean(),
});
export type NotifyPaidResult = z.infer<typeof notifyPaidResultSchema>;

/** The storefront path of a shared bill: `/b/<shareToken>`. */
export function billSharePath(shareToken: string): string {
	return `/b/${shareToken}`;
}
